/**
 * Brick Navigation Registry
 *
 * Maps BricksOS bricks to UI routes for flat sidebar navigation.
 * Replaces GARAGES constant hierarchy with usage-sorted flat list.
 */

// Icon mapping for bricks (Tabler icon names)
export const BRICK_ICONS = {
  assignments: 'IconClipboard',
  clients: 'IconUsers',
  valets: 'IconRun',
  badges: 'IconAward',
  locations: 'IconMapPin',
  events: 'IconCalendar',
  products: 'IconPackage',
  finance: 'IconCurrencyDollar',
  disbursements: 'IconCash',
  payouts: 'IconCoin',
  invoices: 'IconReceipt',
  incidents: 'IconAlertCircle',
  claims: 'IconAlertTriangle',
  contracts: 'IconBriefcase',
  equipment: 'IconTool',
  communications: 'IconMessage',
  onboarding: 'IconUserPlus',
  training: 'IconSchool',
  recruits: 'IconUserPlus',
  schedule: 'IconCalendarStats',
  tickets: 'IconCar',
  governance: 'IconShield',
  evaluation: 'IconStar',
  insurance: 'IconShield',
  // Views (not pure bricks but have UI)
  'valet-dashboard': 'IconHome',
  'valet-wallet': 'IconCash',
  'valet-leaderboard': 'IconTrophy',
  'knowledge-base': 'IconBook',
  mailroom: 'IconMail',
  leadership: 'IconCrown',
  world: 'IconWorld',
  shuttle: 'IconBus',
  gseater: 'IconRun',
  apparel: 'IconShirt',
  'apparel-admin': 'IconShirt',
  'crm': 'IconRocket',
  agents: 'IconRobot',
  'odv-matrix': 'IconTerminal2'
};

/**
 * Brick Navigation Registry
 *
 * Each brick entry contains:
 * - name: Display name in sidebar
 * - icon: Tabler icon component name
 * - route: Primary route path (null = no UI yet)
 * - requiredBadges: Array of badge types that grant access
 * - status: 'active' | 'coming-soon' | 'hidden'
 */
export const BRICK_NAVIGATION = {
  // === CORE OPERATIONS ===
  assignments: {
    name: 'Assignments',
    icon: 'IconClipboard',
    route: '/operations/assignments',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  'open-assignments': {
    name: 'Open Assignments',
    icon: 'IconAlertTriangle',
    route: '/operations/open-assignments',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  'client-portal-viewer': {
    name: 'Client Portal Viewer',
    icon: 'IconEye',
    route: '/clients/portal',
    requiredBadges: ['kot', 'council'],
    status: 'active'
  },
  services: {
    name: 'Services',
    icon: 'IconCalendarStats',
    route: '/operations/services',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  'pay-change-requests': {
    name: 'Pay Change Requests',
    icon: 'IconCurrencyDollar',
    route: '/operations/pay-change-requests',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  'round-table': {
    name: 'Round Table',
    icon: 'IconUsers',
    route: '/operations/round-table',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  clients: {
    name: 'Clients',
    icon: 'IconUsers',
    route: '/operations/clients',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  valets: {
    name: 'Valets',
    icon: 'IconRun',
    route: '/valets',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  events: {
    name: 'Events',
    icon: 'IconCalendar',
    route: '/operations/events',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  locations: {
    name: 'Locations',
    icon: 'IconMapPin',
    route: '/operations/locations',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  products: {
    name: 'Products',
    icon: 'IconPackage',
    route: '/operations/products',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  incidents: {
    name: 'Incidents',
    icon: 'IconAlertCircle',
    route: '/operations/incidents',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },

  // === FINANCE ===
  finance: {
    name: 'Finance',
    icon: 'IconCurrencyDollar',
    route: '/finance/dashboard',
    requiredBadges: ['council'],
    status: 'active'
  },
  disbursements: {
    name: 'Disbursements',
    icon: 'IconCash',
    route: '/finance/disbursements',
    requiredBadges: ['council'],
    status: 'active'
  },
  invoices: {
    name: 'Invoices',
    icon: 'IconReceipt',
    route: '/finance/invoices',
    requiredBadges: ['council'],
    status: 'active'
  },
  collections: {
    name: 'Collections',
    icon: 'IconCalendarStats',
    route: '/finance/collections',
    requiredBadges: ['council'],
    status: 'active'
  },
  'breaking-bread': {
    name: "Breakin' Bread",
    icon: 'IconCoin',
    route: '/finance/breaking-bread',
    requiredBadges: ['council'],
    status: 'active'
  },
  bookkeeping: {
    name: 'Books',
    icon: 'IconReceipt',
    route: '/finance/bookkeeping',
    requiredBadges: ['council'],
    status: 'active'
  },
  'tax-tracking': {
    name: 'Tax Tracking',
    icon: 'IconClipboard',
    route: '/finance/tax-tracking',
    requiredBadges: ['council'],
    status: 'active'
  },

  // === HR ===
  reliability: {
    name: 'Reliability',
    icon: 'IconShieldCheck',
    route: '/hr/valets/reliability',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  papercheck: {
    name: 'Paperwork Check',
    icon: 'IconFileCheck',
    route: '/hr/papercheck',
    requiredBadges: ['kot', 'council'],
    status: 'active'
  },
  badges: {
    name: 'Badges',
    icon: 'IconAward',
    route: '/hr/badges',
    requiredBadges: ['council'],
    status: 'active'
  },
  onboarding: {
    name: 'Onboarding',
    icon: 'IconUserPlus',
    route: '/hr/valet-onboarding',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  ratings: {
    name: 'Ratings',
    icon: 'IconStar',
    route: '/hr/ratings',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  recruits: {
    name: 'Recruits',
    icon: 'IconUserPlus',
    route: '/recruiting',
    requiredBadges: ['kot', 'council'],
    status: 'active'
  },

  // === PARTNERS ===
  partners: {
    name: 'Partners',
    icon: 'IconBriefcase',
    route: '/partners',
    requiredBadges: ['council', 'kot'],
    status: 'active'
  },

  // === ADMIN ===
  users: {
    name: 'Users',
    icon: 'IconUsers',
    route: '/admin/users',
    requiredBadges: ['admin-access'],
    status: 'active'
  },
  'client-portal-admin': {
    name: 'Client Portal Admin',
    icon: 'IconShield',
    route: '/admin/client-portal',
    requiredBadges: ['admin-access'],
    status: 'active'
  },
  claims: {
    name: 'Claims',
    icon: 'IconAlertTriangle',
    route: '/admin/claims',
    requiredBadges: ['admin-access', 'kot', 'council'],
    status: 'active'
  },
  contracts: {
    name: 'Contracts',
    icon: 'IconBriefcase',
    route: '/admin/contracts',
    requiredBadges: ['admin-access'],
    status: 'active'
  },
  mailroom: {
    name: 'Mail Room',
    icon: 'IconMail',
    route: '/mailroom',
    requiredBadges: ['admin-access', 'kot', 'council'],
    status: 'active'
  },
  qc: {
    name: 'Quality Control',
    icon: 'IconClipboardCheck',
    route: '/qc',
    requiredBadges: ['kot', 'council'],
    status: 'active'
  },
  leadership: {
    name: 'Leadership',
    icon: 'IconCrown',
    route: '/leadership',
    requiredBadges: ['admin-access', 'council'],
    status: 'active'
  },
  meetings: {
    name: 'Meetings',
    icon: 'IconCalendarStats',
    route: '/admin/meetings',
    requiredBadges: ['admin-access'],
    status: 'active'
  },
  'odv-matrix': {
    name: 'ODV Matrix',
    icon: 'IconTerminal2',
    route: '/admin/matrix',
    requiredBadges: ['admin-access', 'council'],
    status: 'active'
  },

  // === VALET SELF-SERVICE ===
  'valet-equipment': {
    name: 'Equipment Requests',
    icon: 'IconTool',
    route: '/valet/equipment',
    requiredBadges: ['valet-certified'],
    status: 'active'
  },
  'valet-dashboard': {
    name: 'My Dashboard',
    icon: 'IconHome',
    route: '/valet',
    requiredBadges: ['valet-certified'],
    status: 'active'
  },
  'valet-assignments': {
    name: 'My Assignments',
    icon: 'IconClipboard',
    route: '/valet/assignments',
    requiredBadges: ['valet-certified'],
    status: 'active'
  },
  'valet-wallet': {
    name: 'Wallet',
    icon: 'IconCash',
    route: '/valet/wallet',
    requiredBadges: ['valet-certified'],
    status: 'active'
  },
  'valet-badges': {
    name: 'My Badges',
    icon: 'IconAward',
    route: '/valet/badges',
    requiredBadges: ['valet-certified'],
    status: 'active'
  },
  'valet-leaderboard': {
    name: 'Leaderboard',
    icon: 'IconTrophy',
    route: '/valet/leaderboard',
    requiredBadges: ['valet-certified'],
    status: 'active'
  },
  'school-of-parkology': {
    name: 'School of Parkology',
    icon: 'IconSchool',
    route: '/valet/school',
    requiredBadges: ['valet-certified'],
    status: 'active'
  },

  // === BRANDING ===
  proposals: {
    name: 'Proposals',
    icon: 'IconBriefcase',
    route: '/branding/proposals',
    requiredBadges: ['council'],
    status: 'active'
  },

  // === COMMS ===
  sms: {
    name: 'SMS Center',
    icon: 'IconMessage',
    route: '/sms',
    requiredBadges: ['odv-phone', 'admin-phones'],
    status: 'active'
  },
  comms: {
    name: 'Communications Timeline',
    icon: 'IconSpeakerphone',
    route: '/comms',
    requiredBadges: ['kot', 'council'],
    status: 'active'
  },
  phone: {
    name: 'Phone',
    icon: 'IconPhone',
    route: '/phone',
    requiredBadges: ['kot', 'council'],
    status: 'active'
  },

  // === TOOLS ===
  equipment: {
    name: 'Equipment',
    icon: 'IconTool',
    route: '/admin/equipment',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  apparel: {
    name: 'ODV Apparel',
    icon: 'IconShirt',
    route: '/valet/apparel',
    requiredBadges: ['valet-certified', 'captain', 'kot', 'council'],
    status: 'active'
  },
  'apparel-admin': {
    name: 'Apparel Admin',
    icon: 'IconShirt',
    route: '/branding/apparel',
    requiredBadges: ['council', 'apparel-boss', 'admin-access'],
    status: 'active'
  },
  gseater: {
    name: 'GSeater',
    icon: 'IconRun',
    route: '/operations/gseater',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  shuttle: {
    name: 'Shuttles',
    icon: 'IconBus',
    route: '/operations/shuttle',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },
  world: {
    name: 'ODV World',
    icon: 'IconWorld',
    route: '/operations/world',
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'active'
  },

  // === KNOWLEDGE ===
  'knowledge-base': {
    name: 'Knowledge Base',
    icon: 'IconBook',
    route: '/knowledge-base',
    requiredBadges: ['valet-certified', 'captain', 'kot', 'council', 'admin-access'],
    status: 'active'
  },
  'crm': {
    name: 'Marketing',
    icon: 'IconRocket',
    route: '/marketing',
    requiredBadges: ['council'],
    status: 'active'
  },

  // === TASKS ===
  'task-bucket': {
    name: 'Task Bucket',
    icon: 'IconList',
    route: '/admin/tasks',
    requiredBadges: ['kot', 'council'],
    status: 'active'
  },

  // === AI AGENTS ===
  agents: {
    name: 'Agents',
    icon: 'IconRobot',
    route: '/agents',
    requiredBadges: ['council'],
    status: 'active'
  },
  'voice-analytics': {
    name: 'Voice Analytics',
    icon: 'IconSpeakerphone',
    route: '/agents/voice-analytics',
    requiredBadges: ['council'],
    status: 'active'
  },

  // === COMING SOON (Bricks without full UI) ===
  training: {
    name: 'Training',
    icon: 'IconSchool',
    route: null,
    requiredBadges: ['kot', 'council'],
    status: 'coming-soon'
  },
  evaluation: {
    name: 'Evaluation',
    icon: 'IconStar',
    route: null,
    requiredBadges: ['kot', 'council'],
    status: 'coming-soon'
  },
  schedule: {
    name: 'Schedule',
    icon: 'IconCalendarStats',
    route: null,
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'coming-soon'
  },
  tickets: {
    name: 'Tickets',
    icon: 'IconCar',
    route: null,
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'coming-soon'
  },
  communications: {
    name: 'Communications',
    icon: 'IconMessage',
    route: null,
    requiredBadges: ['captain', 'kot', 'council'],
    status: 'coming-soon'
  },
  insurance: {
    name: 'Insurance',
    icon: 'IconShield',
    route: null,
    requiredBadges: ['council'],
    status: 'coming-soon'
  },
  governance: {
    name: 'Governance',
    icon: 'IconShield',
    route: null,
    requiredBadges: ['council'],
    status: 'coming-soon'
  }
};

/**
 * Get all bricks as an array, filtered by status
 */
export function getAllBricks(includeComingSoon = true) {
  return Object.entries(BRICK_NAVIGATION)
    .filter(([, brick]) =>
      brick.status === 'active' || (includeComingSoon && brick.status === 'coming-soon')
    )
    .map(([id, brick]) => ({ id, ...brick }));
}

/**
 * Filter bricks by user badges
 */
export function filterBricksByBadges(bricks, userBadges) {
  if (!userBadges || userBadges.length === 0) return [];

  // Check for KOT badge which grants broader access
  const hasKOT = userBadges.includes('kot') ||
    userBadges.some(b => b.startsWith('kot-'));
  const hasCouncil = userBadges.includes('council');

  return bricks.filter(brick => {
    // Council and KOT get access to most things
    if (hasCouncil || hasKOT) return true;

    // Check if user has any of the required badges
    if (!brick.requiredBadges || brick.requiredBadges.length === 0) return true;
    return brick.requiredBadges.some(badge => userBadges.includes(badge));
  });
}

/**
 * Sort bricks by usage frequency
 * @param {Array} bricks - Array of brick objects with id
 * @param {Object} usageData - Object with { brickId: { count, lastAccess } }
 */
export function sortBricksByUsage(bricks, usageData = {}) {
  return [...bricks].sort((a, b) => {
    const aUsage = usageData[a.id]?.count || 0;
    const bUsage = usageData[b.id]?.count || 0;

    // Primary: by usage count (descending)
    if (bUsage !== aUsage) return bUsage - aUsage;

    // Secondary: active before coming-soon
    if (a.status !== b.status) {
      return a.status === 'active' ? -1 : 1;
    }

    // Tertiary: alphabetical
    return a.name.localeCompare(b.name);
  });
}

/**
 * Find brick by route path
 */
export function getBrickByRoute(path) {
  if (!path) return null;

  // Normalize path
  const normalizedPath = path.replace(/\/+$/, '') || '/';

  for (const [id, brick] of Object.entries(BRICK_NAVIGATION)) {
    if (brick.route && normalizedPath.startsWith(brick.route)) {
      return { id, ...brick };
    }
  }
  return null;
}
